import { ApiError } from '@gorila-shared-ui/components';
import { ApiEndpoint } from '../types/api';
import { ChannelId } from '../types/gptTypes';
import {
  MediaStream,
  StreamingCaptureResponse,
  StreamingCommand,
  StreamingCommandResponse,
  StreamingTimeline,
  StreamType,
  TimelineAvailability,
} from '../types/streaming';
import { getEndDateFromDate, getStartDateFromDate } from '../utils/dates';
import { deleteRequest, getRequest, postRequest } from './api';
import {
  deviceConnectionStatusUrl,
  devicesConnectionStatusUrl,
  streamingCaptureUrl,
  streamingCommandsUrl,
  streamingTimelineAvailabilityUrl,
  streamingTimelineUrl,
  streamingVideoCaptureUrl,
} from './urls/streamingUrls';

export const getStreamingCapture = async (
  assetId: string,
  clientId: string,
  subClientId?: string,
): Promise<{ data?: StreamingCaptureResponse; error?: ApiError }> => {
  const response = await getRequest(streamingCaptureUrl(assetId), { queryParams: { clientId, subClientId } });
  return {
    data: response?.data,
    error: response?.error,
  };
};

export const getStreamingTimeline = async (
  assetId: string,
  clientId: string,
  subClientId: string,
  startDate: number,
  endDate: number,
): Promise<{ data?: StreamingTimeline; error?: ApiError }> => {
  const queryParams = {
    clientId,
    subClientId,
    startDate: getStartDateFromDate(new Date(startDate)),
    endDate: getEndDateFromDate(new Date(endDate)),
  };
  const response = await getRequest(streamingTimelineUrl(assetId), {
    queryParams,
  });
  return {
    data: response?.data,
    error: response?.error,
  };
};

export const getStreamingTimelineAvailability = async (
  assetId: string,
  clientId: string,
  subClientId: string,
  startDate: number,
  endDate: number,
): Promise<{ data?: TimelineAvailability; error?: ApiError }> => {
  const queryParams = {
    clientId,
    subClientId,
    startDate: getStartDateFromDate(new Date(startDate)),
    endDate: getEndDateFromDate(new Date(endDate)),
  };
  const response = await getRequest(streamingTimelineAvailabilityUrl(assetId), {
    queryParams,
  });
  return {
    data: response?.data,
    error: response?.error,
  };
};

export const getStreamingVideoCapture = async (params: {
  assetId: string;
  channelId: ChannelId;
  clientId: string;
  startDate: number;
  subClientId?: string;
  duration?: number;
}): Promise<{ error?: ApiError }> => {
  const { assetId, channelId, clientId, startDate, duration, subClientId } = params;
  const queryParams = {
    clientId,
    subClientId,
    startDate,
    duration,
  };
  const response = await getRequest(
    streamingVideoCaptureUrl(assetId, channelId),
    {
      queryParams,
    },
    'text',
  );
  return {
    error: response?.error,
  };
};

export const postStreamingCommand = async ({
  assetId,
  channelId,
  command,
  clientId,
  duration,
  fromTime,
  ident,
  streamType,
  mediaStream,
  subClientId,
  audio,
}: {
  assetId: string;
  channelId: ChannelId;
  command: StreamingCommand;
  mediaStream?: MediaStream;
  clientId?: string;
  duration?: number;
  fromTime?: number;
  ident?: string;
  streamType?: StreamType;
  subClientId?: string;
  audio?: boolean;
}): Promise<{ streaming?: StreamingCommandResponse; error?: ApiError }> => {
  const response = await postRequest(
    streamingCommandsUrl,
    {
      body: {
        assetId,
        channelId,
        command,
        clientId,
        duration,
        fromTime,
        ident,
        mediaStream,
        streamType,
        subClientId,
        audio,
      },
    },
    ApiEndpoint.core,
  );

  return {
    streaming: response?.data,
    error: response?.error,
  };
};

export const getDeviceConnectionStatus = async ({
  assetId,
  ident,
  clientId,
  subClientId,
}: {
  assetId: string;
  clientId?: string;
  subClientId?: string;
  ident?: string;
}): Promise<{ connected?: boolean; error?: ApiError }> => {
  const queryParams = {
    assetId,
    clientId,
    subClientId,
    ident,
  };
  const response = await getRequest(
    devicesConnectionStatusUrl,
    {
      queryParams,
    },
    undefined,
    ApiEndpoint.core,
  );
  return {
    connected: response?.data?.connected,
    error: response?.error,
  };
};

export const deleteDeviceConnection = async ({
  connectionId,
  assetId,
  ident,
  clientId,
  subClientId,
}: {
  connectionId: string;
  assetId: string;
  clientId?: string;
  subClientId?: string;
  ident?: string;
}): Promise<{ error?: ApiError }> => {
  const queryParams = {
    assetId,
    clientId,
    subClientId,
    ident,
  };
  const response = await deleteRequest(
    deviceConnectionStatusUrl(connectionId),
    {
      queryParams,
    },
    ApiEndpoint.core,
  );
  return {
    error: response?.error,
  };
};
